// Sentry initialization must run before React mounts so the error
// boundary captures startup errors. Module side-effect import; the file
// is a no-op when VITE_SENTRY_DSN is unset.
import { Sentry } from './sentry';

// CookieYes consent-management loader. Env-gated (prod) module side-effect;
// no-op when VITE_COOKIEYES_SITE_ID is unset.
import './cookieyes';

// Google Tag Manager loader. Env-gated (prod) module side-effect; no-op
// when VITE_GTM_ID is unset. Imported after `./cookieyes` so the consent
// script loads first and can gate the tags GTM fires.
import './gtm';

// Stale-bundle recovery. After a deploy, a still-open tab's index.html
// references the previous build's chunk hashes (e.g. `index-5GmBedkL.js`);
// the server no longer has those files. Any navigation that triggers a
// dynamic import (route lazy-load, code-split component) attempts to
// fetch the missing chunk and fails. The reload mechanics live in
// `./lib/stale-bundle`; the same reload goes to the locale catalog loader
// through `bootApp`.
//
// Two listeners, two coverage shapes:
//
//   1. `vite:preloadError` — fired by Vite's own `__vitePreload` wrapper
//      when the import it dispatched 404s. Covers route-tree imports the
//      Vite build-time transform wraps. The wrapper re-throws the error
//      after firing the event, so without this listener TanStack Router's
//      CatchBoundary would render "Something went wrong!".
//
//   2. `unhandledrejection` — fired by the browser for any promise reject
//      that nothing else handles. Covers dynamic imports that DIDN'T go
//      through `__vitePreload`: TanStack's own preload paths, runtime
//      `import()` from app code that wasn't transformed, browser-native
//      module resolution, and cases where the wrapper's event was
//      preventDefault'd downstream.
import { isStaleBundleError, staleBundleReload } from './lib/stale-bundle';
import { readStorage, writeStorage } from './lib/browser-storage';

const reloadForStaleBundle = staleBundleReload({
  read: (key) => readStorage('session', key),
  write: (key, value) => writeStorage('session', key, value),
  now: Date.now,
  reload: () => window.location.reload(),
});

window.addEventListener('vite:preloadError', () => reloadForStaleBundle());
window.addEventListener('unhandledrejection', (event) => {
  if (isStaleBundleError(event.reason)) reloadForStaleBundle();
});

import '@leanin/ui/tokens.css';
import './globals.scss';
import './server-body.scss';
import {
  isShowingServerBody,
  serverBodyRequestTimeoutMs,
  type Schedule,
  type ServerBodyEnvironment,
} from './lib/server-body-handoff';
import { setApiRequestTimeout } from './lib/api-client';
import { trackPageViewed } from './lib/analytics/events';
import { subscribePageViews } from './lib/analytics/page-views';
import { PAGE_SEO_SEED_ID, parsePageSeoSeed } from './lib/page-seo-seed';
import { bootApp, createAppQueryClient, createAppRouter } from './app';

const schedule: Schedule = (run, delayMs) => {
  const timer = window.setTimeout(run, delayMs);
  return () => window.clearTimeout(timer);
};

const queryClient = createAppQueryClient(
  document,
  parsePageSeoSeed(document.getElementById(PAGE_SEO_SEED_ID)?.textContent ?? null),
);
const router = createAppRouter(queryClient, document, (error, context) =>
  Sentry.captureException(error, context),
);

// A server-composed page (server-body.ts) boots into a hidden root behind its
// server body; its route component reveals the root when the page is ready.
const serverBody: ServerBodyEnvironment | null = isShowingServerBody(document)
  ? {
      document,
      schedule,
      report: ({ event, ...extra }) => Sentry.captureMessage(event, { level: 'warning', extra }),
      isOnline: () => navigator.onLine,
      sample: Math.random,
    }
  : null;

setApiRequestTimeout({ timeoutMs: () => serverBodyRequestTimeoutMs(document), schedule });

subscribePageViews({ router, queryClient, doc: document, track: trackPageViewed });

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('No #root element');

// Block initial React render until the critical brand fonts are loaded,
// so the first paint already uses Avenir Next / Newsreader — no
// glyph-shape swap when the brand font lands a few hundred ms in.
//
// Why the FontFace-constructor approach (not document.fonts.load by
// descriptor): in Vite dev mode the SCSS is injected via a JS-side
// <style> element which the browser parses asynchronously. Calling
// `document.fonts.load('400 14px "Avenir Next"')` at this point
// short-circuits — no matching @font-face is registered yet, the API
// resolves with an empty array immediately, and we'd render with
// fallback. Constructing FontFace objects directly bypasses that race:
// each face fetches from its URL whether or not the CSS has been
// parsed yet, and the resulting FontFace becomes the canonical entry
// in document.fonts for that family/weight combination.
//
// The URLs and weight descriptors here MUST mirror the @font-face
// declarations in globals.scss exactly (same family name, same weight
// range) so the CSS-side declaration doesn't introduce a duplicate
// face that competes with the JS-loaded one.
//
// The cap on this wait is `FONT_READY_TIMEOUT_MS` in app.tsx.

const FIRST_PAINT_FONTS: ReadonlyArray<{
  family: string;
  url: string;
  weight: string;
}> = [
  // Body / closed composer prompt / Caption — every page's running text.
  { family: 'Avenir Next', url: '/fonts/AvenirNext-Regular.woff2', weight: '400' },
  // Label / Button / Pill / Tab pill / Field label — every UI control.
  // Weight range covers the design system's `--type-label-weight: 600`
  // and any direct `700` request through the same Demi file.
  { family: 'Avenir Next', url: '/fonts/AvenirNext-Demi.woff2', weight: '600 700' },
  // Heading / Display — page titles, panel headers, hero text.
  {
    family: 'Newsreader',
    url: "/fonts/Newsreader-VariableFont_opsz,wght.woff2",
    weight: '200 800',
  },
];

async function loadCriticalFonts(): Promise<void> {
  if (!('fonts' in document)) return;
  if (typeof FontFace === 'undefined') return;
  await Promise.all(
    FIRST_PAINT_FONTS.map(async ({ family, url, weight }) => {
      const face = new FontFace(family, `url(${url}) format('woff2')`, {
        weight,
        style: 'normal',
        display: 'swap',
      });
      const loaded = await face.load();
      document.fonts.add(loaded);
    }),
  );
}

void bootApp({
  rootEl,
  router,
  queryClient,
  clerkPublishableKey: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY ?? '',
  schedule,
  loadCriticalFonts,
  serverBody,
  reloadForStaleBundle,
});
